import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Lock, LockOpen, ShieldAlert, ShieldBan, ShieldCheck, X } from 'lucide-react';
import { api } from '@/lib/api';
import { getDisabledPlugins } from '@/lib/plugins';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { RolePicker } from '@/components/RolePicker';
import { UserPicker } from '@/components/UserPicker';
import { UserMention } from '@/components/UserMention';
import { BackLink, useConfigPage } from './config/_shared';

/**
 * Server-Schutz (Verwaltung) — Anti-Nuke + Anti-Raid. Settings in
 * moduleConfigs.protection (same shape, defaults and number ranges as bot
 * plugins/Admin/lib/protection.js); the bot does the work
 * (plugins/Admin/lib/antiNuke.js, antiRaid.js) and writes the incidents
 * listed below. Off until switched on here; the Admin-Panel switches
 * "Anti-Nuke" / "Anti-Raid" override.
 */

interface Threshold {
  enabled: boolean;
  count: number;
  seconds: number;
}
interface AntiRaid {
  enabled: boolean;
  joinWave: { enabled: boolean; count: number; seconds: number; lockdownMinutes: number };
  newAccounts: { enabled: boolean; minDays: number };
  spam: { enabled: boolean; repeats: number; seconds: number; mentions: number; timeoutMinutes: number; raidAccounts: number };
}
interface ProtectionConfig {
  alertChannelId: string | null;
  pingRoleId: string | null;
  trustedUserIds: string[];
  trustedRoleIds: string[];
  antiNuke: {
    enabled: boolean;
    deletes: Threshold;
    bans: Threshold;
    dangerousPerms: boolean;
    botsWebhooks: boolean;
  };
  antiRaid: AntiRaid;
}

const DEFAULTS: ProtectionConfig = {
  alertChannelId: null,
  pingRoleId: null,
  trustedUserIds: [],
  trustedRoleIds: [],
  antiNuke: {
    enabled: false,
    deletes: { enabled: true, count: 3, seconds: 60 },
    bans: { enabled: true, count: 5, seconds: 60 },
    dangerousPerms: true,
    botsWebhooks: true,
  },
  antiRaid: {
    enabled: false,
    joinWave: { enabled: true, count: 10, seconds: 30, lockdownMinutes: 30 },
    newAccounts: { enabled: false, minDays: 7 },
    spam: { enabled: true, repeats: 3, seconds: 10, mentions: 5, timeoutMinutes: 60, raidAccounts: 3 },
  },
};

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
function merge<T>(defaults: T, value: unknown): T {
  if (!isObj(value)) return defaults;
  const out: Obj = { ...(defaults as Obj) };
  for (const [k, v] of Object.entries(value)) out[k] = isObj((defaults as Obj)[k]) ? merge((defaults as Obj)[k], v) : v;
  return out as T;
}

interface Incident {
  id: string;
  at: string;
  kind: 'nuke' | 'raid';
  trigger: string;
  actorId: string | null;
  actorName: string | null;
  summary: string;
  details: { actions?: string[]; restored?: string[]; failed?: string[] };
}

export function ProtectionPage() {
  const { guildId, settingsQuery, saveMut, currentConfig } = useConfigPage('protection');
  const [cfg, setCfg] = useState<ProtectionConfig>(DEFAULTS);

  useEffect(() => {
    if (settingsQuery.data) setCfg(merge(DEFAULTS, currentConfig));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data]);

  const status = useQuery({
    queryKey: ['protection-status', guildId],
    queryFn: () =>
      api<{
        botRole: { id: string; name: string } | null;
        rolesAbove: Array<{ id: string; name: string }>;
        lockdownUntil: string | null;
      }>(`/api/guilds/${guildId}/protection/status`),
    // While a lockdown runs: notice when it ends by itself.
    refetchInterval: (q) => (q.state.data?.lockdownUntil ? 30_000 : false),
  });

  if (settingsQuery.isLoading) return <p className="text-muted-foreground">Lade…</p>;

  const disabled = getDisabledPlugins(settingsQuery.data?.settings.moduleConfigs);
  const nuke = cfg.antiNuke;
  const setNuke = (patch: Partial<ProtectionConfig['antiNuke']>) => setCfg((c) => ({ ...c, antiNuke: { ...c.antiNuke, ...patch } }));
  const setLimit = (key: 'deletes' | 'bans', patch: Partial<Threshold>) => setNuke({ [key]: { ...nuke[key], ...patch } });
  const raid = cfg.antiRaid;
  const setRaid = <K extends 'joinWave' | 'newAccounts' | 'spam'>(key: K, patch: Partial<AntiRaid[K]>) =>
    setCfg((c) => ({ ...c, antiRaid: { ...c.antiRaid, [key]: { ...c.antiRaid[key], ...patch } } }));
  const off = [!nuke.enabled && 'Anti-Nuke', !raid.enabled && 'Anti-Raid'].filter((x): x is string => !!x);

  return (
    <div className="mx-auto max-w-3xl">
      <BackLink guildId={guildId} />
      <h1 className="mb-6 text-2xl font-semibold">Server-Schutz</h1>

      {status.data?.lockdownUntil && <LockdownBanner guildId={guildId} until={status.data.lockdownUntil} />}

      {off.length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <span>
            <b>{off.join(' und ')}</b> {off.length > 1 ? 'sind' : 'ist'} aus — dagegen ist der Server nicht geschützt.
            Unten einschalten und vorher die vertrauten Leute eintragen.
          </span>
        </div>
      )}

      {!!status.data?.rolesAbove.length && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <span>
            Über der Bot-Rolle „{status.data.botRole?.name}“ stehen:{' '}
            <b>{status.data.rolesAbove.map((r) => r.name).join(', ')}</b>. Wer eine davon hat, kann von Anti-Nuke
            nicht entmachtet werden. Zieh die Bot-Rolle in Discord (Servereinstellungen → Rollen) ganz nach oben.
          </span>
        </div>
      )}

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck className="h-5 w-5 text-primary" /> Anti-Nuke
          </CardTitle>
          <CardDescription>
            Stoppt, wenn jemand mit Rechten (oder ein gekaperter Account / fremder Bot) den Server zerlegt. Der Täter
            verliert sofort alle Rollen (ein Bot wird gekickt), danach macht der Bot rückgängig, was geht: gelöschte
            Kanäle und Rollen neu (Nachrichten bleiben weg), Bans aufheben, Rechte zurücknehmen, Bot/Webhook
            entfernen. Ausgenommen: Server-Owner, der Bot selbst und die Ausnahmen unten.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {disabled.includes('antiNuke') && (
            <p className="rounded-md border border-amber-500/50 p-2 text-sm text-amber-600 dark:text-amber-400">
              Im Admin-Panel ist der Server-Schalter „Anti-Nuke“ aus — solange greift hier nichts.
            </p>
          )}
          <Tile title="Anti-Nuke aktiv" hint="Hauptschalter für diesen Server" on={nuke.enabled} onChange={(v) => setNuke({ enabled: v })} strong />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Tile
              title="Kanäle/Rollen löschen"
              hint={<LimitInputs value={nuke.deletes} onChange={(p) => setLimit('deletes', p)} />}
              on={nuke.deletes.enabled}
              onChange={(v) => setLimit('deletes', { enabled: v })}
            />
            <Tile
              title="Massen-Bans/Kicks"
              hint={<LimitInputs value={nuke.bans} onChange={(p) => setLimit('bans', p)} />}
              on={nuke.bans.enabled}
              onChange={(v) => setLimit('bans', { enabled: v })}
            />
            <Tile
              title="Gefährliche Rechte vergeben"
              hint="Administrator, Server/Rollen/Kanäle verwalten, Bannen, Kicken, Webhooks — an Rolle, @everyone oder Person. Greift sofort."
              on={nuke.dangerousPerms}
              onChange={(v) => setNuke({ dangerousPerms: v })}
            />
            <Tile
              title="Fremde Bots & Webhooks"
              hint="Bot hinzufügen oder Webhook anlegen. Greift sofort."
              on={nuke.botsWebhooks}
              onChange={(v) => setNuke({ botsWebhooks: v })}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ShieldBan className="h-5 w-5 text-primary" /> Anti-Raid
          </CardTitle>
          <CardDescription>
            Stoppt Beitritts- und Spam-Wellen. Bei einem Raid gibt es einen Lockdown: Discord pausiert die
            Einladungen, die Welle wird gekickt (mit DM), der Alarm kommt mit Knopf „Lockdown beenden“ — sonst endet
            er nach der eingestellten Zeit. Vom Spam-Schutz ausgenommen: alle mit „Nachrichten verwalten“ und die
            Ausnahmen unten.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {disabled.includes('antiRaid') && (
            <p className="rounded-md border border-amber-500/50 p-2 text-sm text-amber-600 dark:text-amber-400">
              Im Admin-Panel ist der Server-Schalter „Anti-Raid“ aus — solange greift hier nichts.
            </p>
          )}
          <Tile
            title="Anti-Raid aktiv"
            hint="Hauptschalter für diesen Server"
            on={raid.enabled}
            onChange={(v) => setCfg((c) => ({ ...c, antiRaid: { ...c.antiRaid, enabled: v } }))}
            strong
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Tile
              title="Beitritts-Welle"
              hint={
                <span className="mt-1 inline-flex flex-wrap items-center gap-1">
                  ab <Num value={raid.joinWave.count} min={3} max={100} onChange={(count) => setRaid('joinWave', { count })} />
                  Beitritten in <Num value={raid.joinWave.seconds} min={5} max={600} onChange={(seconds) => setRaid('joinWave', { seconds })} wide />
                  Sek. → Lockdown für
                  <Num
                    value={raid.joinWave.lockdownMinutes}
                    min={5}
                    max={1440}
                    onChange={(lockdownMinutes) => setRaid('joinWave', { lockdownMinutes })}
                    wide
                  />
                  Min.
                </span>
              }
              on={raid.joinWave.enabled}
              onChange={(enabled) => setRaid('joinWave', { enabled })}
            />
            <Tile
              title="Neue Accounts abweisen"
              hint={
                <span className="mt-1 inline-flex flex-wrap items-center gap-1">
                  jünger als <Num value={raid.newAccounts.minDays} min={1} max={90} onChange={(minDays) => setRaid('newAccounts', { minDays })} />
                  Tage → Kick mit DM „versuch es ab …“
                </span>
              }
              on={raid.newAccounts.enabled}
              onChange={(enabled) => setRaid('newAccounts', { enabled })}
            />
          </div>
          <Tile
            title="Spam"
            hint={
              <span className="mt-1 inline-flex flex-wrap items-center gap-1">
                gleiche Nachricht <Num value={raid.spam.repeats} min={2} max={20} onChange={(repeats) => setRaid('spam', { repeats })} />× in
                <Num value={raid.spam.seconds} min={3} max={120} onChange={(seconds) => setRaid('spam', { seconds })} /> Sek. oder ab
                <Num value={raid.spam.mentions} min={2} max={50} onChange={(mentions) => setRaid('spam', { mentions })} /> Erwähnungen →
                löschen + <Num value={raid.spam.timeoutMinutes} min={1} max={10080} onChange={(timeoutMinutes) => setRaid('spam', { timeoutMinutes })} wide />
                Min. Timeout. Ab <Num value={raid.spam.raidAccounts} min={2} max={20} onChange={(raidAccounts) => setRaid('spam', { raidAccounts })} />
                Spammern gleichzeitig zusätzlich Lockdown.
              </span>
            }
            on={raid.spam.enabled}
            onChange={(enabled) => setRaid('spam', { enabled })}
          />
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">Alarm & Ausnahmen</CardTitle>
          <CardDescription>
            Wohin der Alarm geht (zusätzlich immer als DM an den Server-Owner) und wer nie als Täter oder Spammer
            zählt — z. B. Mods, die öfter aufräumen, oder Bots, die selbst bannen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>Alarm-Channel</Label>
              <ChannelPicker
                guildId={guildId}
                value={cfg.alertChannelId}
                onChange={(v) => setCfg((c) => ({ ...c, alertChannelId: v }))}
                types={[CHANNEL_TYPE.GUILD_TEXT]}
              />
            </div>
            <div>
              <Label>Ping-Rolle im Alarm (optional)</Label>
              <RolePicker guildId={guildId} value={cfg.pingRoleId} onChange={(v) => setCfg((c) => ({ ...c, pingRoleId: v }))} />
            </div>
          </div>
          <TrustedList
            guildId={guildId}
            label="Vertraute Personen"
            ids={cfg.trustedUserIds}
            onChange={(ids) => setCfg((c) => ({ ...c, trustedUserIds: ids }))}
            kind="user"
          />
          <TrustedList
            guildId={guildId}
            label="Vertraute Rollen"
            ids={cfg.trustedRoleIds}
            onChange={(ids) => setCfg((c) => ({ ...c, trustedRoleIds: ids }))}
            kind="role"
          />
        </CardContent>
      </Card>

      <div className="mb-8 flex items-center justify-end gap-3">
        {saveMut.isSuccess && !saveMut.isPending && <span className="text-sm text-success">Gespeichert ✓</span>}
        {saveMut.error && <span className="text-sm text-destructive">{(saveMut.error as Error).message}</span>}
        <Button disabled={saveMut.isPending} onClick={() => saveMut.mutate({ moduleConfig: cfg as unknown as Record<string, unknown> })}>
          {saveMut.isPending ? 'Speichere…' : 'Speichern'}
        </Button>
      </div>

      <Incidents guildId={guildId} />
    </div>
  );
}

function Tile({
  title,
  hint,
  on,
  onChange,
  strong,
}: {
  title: string;
  hint: React.ReactNode;
  on: boolean;
  onChange: (on: boolean) => void;
  strong?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
      <div className="min-w-0">
        <div className={on ? (strong ? 'text-sm font-medium' : 'text-sm') : 'text-sm text-muted-foreground'}>{title}</div>
        <div className="text-[11px] text-muted-foreground">{hint}</div>
      </div>
      <Switch checked={on} onChange={(e) => onChange(e.target.checked)} aria-label={title} />
    </div>
  );
}

/** Small number input inside a tile hint; min/max = the bot's ranges (lib/protection.js LIMITS). */
function Num({
  value,
  min,
  max,
  onChange,
  wide,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
  wide?: boolean;
}) {
  return (
    <Input
      type="number"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(Math.min(max, Math.max(min, Number(e.target.value) || min)))}
      className={`h-7 px-2 text-xs ${wide ? 'w-16' : 'w-14'}`}
    />
  );
}

function LimitInputs({ value, onChange }: { value: Threshold; onChange: (p: Partial<Threshold>) => void }) {
  return (
    <span className="mt-1 inline-flex flex-wrap items-center gap-1">
      ab <Num value={value.count} min={1} max={50} onChange={(count) => onChange({ count })} />
      in <Num value={value.seconds} min={5} max={600} onChange={(seconds) => onChange({ seconds })} wide />
      Sekunden
    </span>
  );
}

const clockFmt = (iso: string) => {
  const d = new Date(iso);
  const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return d.toDateString() === new Date().toDateString()
    ? `${time} Uhr`
    : `${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} ${time} Uhr`;
};

/** Invites paused (Anti-Raid lockdown, or by hand in Discord) — end it from here. */
function LockdownBanner({ guildId, until }: { guildId: string; until: string }) {
  const qc = useQueryClient();
  const end = useMutation({
    mutationFn: () => api(`/api/guilds/${guildId}/protection/lockdown/end`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['protection-status', guildId] });
      void qc.invalidateQueries({ queryKey: ['protection-incidents', guildId] });
    },
  });
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
      <Lock className="h-4 w-4 shrink-0 text-destructive" />
      <span className="flex-1 basis-56">
        <b>Lockdown läuft</b> — Einladungen sind bis {clockFmt(until)} pausiert, wer trotzdem beitritt, wird
        gekickt.
      </span>
      {end.error && <span className="w-full text-xs text-destructive">{(end.error as Error).message}</span>}
      <Button size="sm" variant="outline" disabled={end.isPending} onClick={() => end.mutate()}>
        <LockOpen className="mr-1.5 h-4 w-4" />
        {end.isPending ? 'Beende…' : 'Lockdown beenden'}
      </Button>
    </div>
  );
}

function TrustedList({
  guildId,
  label,
  ids,
  onChange,
  kind,
}: {
  guildId: string;
  label: string;
  ids: string[];
  onChange: (ids: string[]) => void;
  kind: 'user' | 'role';
}) {
  const [pick, setPick] = useState<string | null>(null);
  const roles = useQuery({
    queryKey: ['guild-roles', guildId],
    queryFn: () => api<{ roles: Array<{ id: string; name: string }> }>(`/api/guilds/${guildId}/roles`),
    enabled: kind === 'role',
  });
  const add = () => {
    if (pick && !ids.includes(pick)) onChange([...ids, pick]);
    setPick(null);
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2">
        <div className="min-w-0 flex-1">
          {kind === 'user' ? (
            <UserPicker guildId={guildId} value={pick} onChange={setPick} />
          ) : (
            <RolePicker guildId={guildId} value={pick} onChange={setPick} />
          )}
        </div>
        <Button variant="outline" disabled={!pick} onClick={add}>
          Hinzufügen
        </Button>
      </div>
      {ids.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {ids.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 rounded-full border bg-muted/50 px-2 py-0.5 text-xs">
              {kind === 'user' ? <UserMention id={id} /> : `@${roles.data?.roles.find((r) => r.id === id)?.name ?? id}`}
              <button
                type="button"
                className="rounded-full p-0.5 text-muted-foreground hover:text-foreground"
                onClick={() => onChange(ids.filter((x) => x !== id))}
                aria-label="Entfernen"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const TRIGGER_LABEL: Record<string, string> = {
  deletes: 'Löschungen',
  bans: 'Bans/Kicks',
  'dangerous-perms': 'Gefährliche Rechte',
  'bot-add': 'Fremder Bot',
  webhook: 'Webhook',
  'join-wave': 'Beitritts-Welle',
  'new-account': 'Neue Accounts',
  spam: 'Spam',
  'spam-wave': 'Spam-Welle',
};

function Incidents({ guildId }: { guildId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['protection-incidents', guildId],
    queryFn: () => api<{ incidents: Incident[] }>(`/api/guilds/${guildId}/protection/incidents`),
  });
  const list = data?.incidents ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Vorfälle</CardTitle>
        <CardDescription>Was der Schutz erkannt und getan hat (90 Tage).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading && <p className="text-sm text-muted-foreground">Lade…</p>}
        {!isLoading && list.length === 0 && <p className="text-sm text-muted-foreground">Bisher keine Vorfälle.</p>}
        {list.map((i) => (
          <details key={i.id} className="rounded-md border px-3 py-2 text-sm">
            <summary className="cursor-pointer">
              <span className="text-muted-foreground">{new Date(i.at).toLocaleString('de-DE')}</span>{' '}
              <span className="rounded bg-destructive/15 px-1.5 py-0.5 text-[11px] text-destructive">
                {i.kind === 'nuke' ? 'Anti-Nuke' : 'Anti-Raid'} · {TRIGGER_LABEL[i.trigger] ?? i.trigger}
              </span>{' '}
              {i.summary}
              {i.actorId && (
                <>
                  {' '}
                  — <UserMention id={i.actorId} showAvatar={false} />
                </>
              )}
            </summary>
            <div className="mt-2 space-y-1 text-xs text-muted-foreground">
              {!!i.details.actions?.length && <div>Gemacht: {i.details.actions.join('; ')}</div>}
              {!!i.details.restored?.length && <div>Wiederhergestellt: {i.details.restored.join('; ')}</div>}
              {!!i.details.failed?.length && (
                <div className="text-amber-600 dark:text-amber-400">Nicht möglich: {i.details.failed.join('; ')}</div>
              )}
            </div>
          </details>
        ))}
      </CardContent>
    </Card>
  );
}
